/**
 * DevDocsHome — Developer documentation portal (developers.example.com)
 * Sidebar navigation + category cards / article list
 */

import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';

import { T } from '../styles/tokens';
const API_BASE = '/api/v1/cms';

interface Category { id: string; slug: string; icon: string; name: string; description: string; article_count: number }
interface Article { id: string; slug: string; title: string; excerpt: string; api_endpoint: string; api_method: string }

const ICONS: Record<string, string> = {
  rocket: 'M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09zM12 15l-3-3 10-10 3 3z',
  book: 'M4 19.5A2.5 2.5 0 016.5 17H20M4 19.5A2.5 2.5 0 016.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z',
  code: 'M16 18l6-6-6-6M8 6l-6 6 6 6',
  terminal: 'M4 17l6-6-6-6M12 19h8',
  package: 'M16.5 9.4l-9-5.19M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16zM3.27 6.96L12 12.01l8.73-5.05M12 22.08V12',
};

const METHOD_COLORS: Record<string, string> = {
  GET: T.success, POST: T.primary, PUT: T.warning, DELETE: T.danger, PATCH: T.purple,
};

const S = {
  page: { minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Inter', -apple-system, sans-serif", display: 'flex' } as any,
  sidebar: { width: 260, borderRight: `1px solid ${T.border}`, padding: '24px 0', flexShrink: 0, overflowY: 'auto' as const, position: 'sticky' as const, top: 0, height: '100vh' } as any,
  sidebarTitle: { fontSize: 18, fontWeight: 700, padding: '0 20px', marginBottom: 20, letterSpacing: '-0.02em' },
  searchInput: { width: 'calc(100% - 40px)', margin: '0 20px 16px', padding: '8px 12px', fontSize: 13, background: T.bgCardHover, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: T.text, outline: 'none', boxSizing: 'border-box' as const },
  navItem: (active: boolean) => ({
    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 20px', fontSize: 14,
    color: active ? T.primary : T.textSecondary, cursor: 'pointer', transition: 'all 0.15s',
    background: active ? T.primaryBg : 'transparent', fontWeight: active ? 500 : 400,
    borderLeft: active ? '2px solid #5B8DEF' : '2px solid transparent',
  }),
  navArticle: (active: boolean) => ({
    display: 'block', padding: '5px 20px 5px 50px', fontSize: 13,
    color: active ? T.primary : T.textMuted, cursor: 'pointer', transition: 'color 0.15s',
  }),
  content: { flex: 1, padding: '40px 48px', maxWidth: 900 } as any,
  breadcrumbs: { fontSize: 13, color: T.textMuted, marginBottom: 24, display: 'flex', gap: 8, alignItems: 'center' } as any,
  breadcrumbLink: { color: T.primary, cursor: 'pointer', textDecoration: 'none' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 } as any,
  card: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: 20, cursor: 'pointer', transition: 'border-color 0.15s' } as any,
  cardIcon: { width: 36, height: 36, borderRadius: 8, background: T.primaryBg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 } as any,
  cardTitle: { fontSize: 16, fontWeight: 600, marginBottom: 6 },
  cardDesc: { fontSize: 13, color: T.textMuted, lineHeight: 1.5 },
  cardCount: { fontSize: 12, color: T.primary, marginTop: 8 },
  articleList: { listStyle: 'none', padding: 0, margin: 0 } as any,
  articleItem: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, padding: '14px 18px', marginBottom: 8, cursor: 'pointer', transition: 'border-color 0.15s', display: 'flex', justifyContent: 'space-between', alignItems: 'center' } as any,
  articleTitle: { fontSize: 15, fontWeight: 500 },
  articleExcerpt: { fontSize: 13, color: T.textMuted, marginTop: 4 },
  methodBadge: (method: string) => ({ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: `${METHOD_COLORS[method] || T.textMuted}20`, color: METHOD_COLORS[method] || T.textMuted }),
  skeleton: { background: T.bgCardHover, borderRadius: 8, height: 120, marginBottom: 16 } as any,
};

export function DevDocsHomePage(_props: { path?: string; locale?: string; categorySlug?: string }) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);

  const locale = _props.locale || 'pt-BR';
  const categorySlug = _props.categorySlug;

  useEffect(() => {
    fetch(`${API_BASE}/categories?contentType=dev-docs&locale=${encodeURIComponent(locale)}`)
      .then(r => r.json())
      .then((res: any) => {
        setCategories(res?.data?.categories || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [locale]);

  useEffect(() => {
    if (!categorySlug) { setSelectedCategory(null); setArticles([]); return; }
    fetch(`${API_BASE}/categories/${encodeURIComponent(categorySlug)}?contentType=dev-docs&locale=${encodeURIComponent(locale)}`)
      .then(r => r.json())
      .then((res: any) => {
        setSelectedCategory(res?.data?.category || null);
        setArticles(res?.data?.entries || []);
      })
      .catch(() => {});
  }, [categorySlug, locale]);

  useEffect(() => {
    if (searchQuery.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(() => {
      fetch(`${API_BASE}/search?contentType=dev-docs&q=${encodeURIComponent(searchQuery)}&locale=${encodeURIComponent(locale)}`)
        .then(r => r.json())
        .then((res: any) => setSearchResults(res?.data?.entries || []))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, locale]);

  return (
    <div style={S.page}>
      {/* Sidebar */}
      <nav style={S.sidebar}>
        <div style={S.sidebarTitle}>WhiteLabelClaw Docs</div>
        <input
          style={S.searchInput}
          type="text"
          placeholder="Buscar documentacao..."
          value={searchQuery}
          onInput={(e: any) => setSearchQuery(e.target.value)}
        />
        {categories.map((cat) => (
          <div key={cat.id}>
            <div
              style={S.navItem(cat.slug === categorySlug)}
              onClick={() => route(`/${locale}/${cat.slug}`)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d={ICONS[cat.icon] || ICONS.code} />
              </svg>
              {cat.name || cat.slug}
            </div>
          </div>
        ))}
      </nav>

      {/* Content */}
      <div style={S.content}>
        {/* Search results */}
        {searchQuery.length >= 2 && (
          <div>
            <div style={S.breadcrumbs}>
              <span>Resultados para "{searchQuery}"</span>
            </div>
            {searchResults.length === 0 && <p style={{ color: T.textMuted }}>Nenhum resultado encontrado.</p>}
            <ul style={S.articleList}>
              {searchResults.map((r: any) => (
                <li
                  key={r.id}
                  style={S.articleItem}
                  onClick={() => route(`/${locale}/${r.category_slug}/${r.slug}`)}
                  onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.bgActive; }}
                  onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; }}
                >
                  <div>
                    <div style={S.articleTitle}>{r.title}</div>
                    <div style={S.articleExcerpt}>{r.snippet || r.excerpt}</div>
                  </div>
                  {r.api_method && <span style={S.methodBadge(r.api_method)}>{r.api_method}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Category view */}
        {searchQuery.length < 2 && categorySlug && selectedCategory && (
          <div>
            <div style={S.breadcrumbs}>
              <span style={S.breadcrumbLink} onClick={() => route(`/${locale}`)}>Home</span>
              <span>/</span>
              <span>{selectedCategory.name}</span>
            </div>
            <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>{selectedCategory.name}</h1>
            <p style={{ fontSize: 14, color: T.textMuted, marginBottom: 24 }}>{selectedCategory.description}</p>
            {articles.length === 0 && <p style={{ color: T.textMuted }}>Nenhum artigo nesta categoria.</p>}
            <ul style={S.articleList}>
              {articles.map((a) => (
                <li
                  key={a.id}
                  style={S.articleItem}
                  onClick={() => route(`/${locale}/${categorySlug}/${a.slug}`)}
                  onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.bgActive; }}
                  onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; }}
                >
                  <div>
                    <div style={S.articleTitle}>{a.title || a.slug}</div>
                    {a.excerpt && <div style={S.articleExcerpt}>{a.excerpt}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {a.api_method && <span style={S.methodBadge(a.api_method)}>{a.api_method}</span>}
                    {a.api_endpoint && <span style={{ fontSize: 12, color: T.textMuted, fontFamily: 'monospace' }}>{a.api_endpoint}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Home — category cards */}
        {searchQuery.length < 2 && !categorySlug && (
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8, letterSpacing: '-0.02em' }}>Documentacao para Desenvolvedores</h1>
            <p style={{ fontSize: 15, color: T.textMuted, marginBottom: 32 }}>Tudo que voce precisa para integrar com a API da WhiteLabelClaw.</p>
            {loading && (
              <div style={S.grid}>
                <div style={S.skeleton} />
                <div style={S.skeleton} />
                <div style={S.skeleton} />
              </div>
            )}
            <div style={S.grid}>
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  style={S.card}
                  onClick={() => route(`/${locale}/${cat.slug}`)}
                  onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.bgActive; }}
                  onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; }}
                >
                  <div style={S.cardIcon}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5B8DEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d={ICONS[cat.icon] || ICONS.code} />
                    </svg>
                  </div>
                  <div style={S.cardTitle}>{cat.name || cat.slug}</div>
                  <div style={S.cardDesc}>{cat.description}</div>
                  <div style={S.cardCount}>{cat.article_count} artigo{cat.article_count !== 1 ? 's' : ''}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
